import { useCallback, useEffect, useState } from 'react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useOverviewDashboard } from '../../hooks/useOverviewDashboard';
import { useWorkspaceSyncTool } from '../../hooks/useWorkspaceSyncTool';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { REFRESH_INTERVAL_MS } from '../../utils/constants';
import { fetchExpenseDrilldown } from '../../api/dashboard';
import { AccountingSyncStatus } from '../layout/AccountingSyncStatus';
import { SyncStatusBar } from '../layout/SyncStatusBar';
import { EmptyState } from '../ui/EmptyState';
import { ErrorState } from '../ui/ErrorState';
import { CardSkeleton, LoadingSkeleton } from '../ui/LoadingSkeleton';
import { KPICards } from './KPICards';
import { MonthlyPnLChart } from './MonthlyPnLChart';
import { RevenueTrendChart } from './RevenueTrendChart';
import { CashFlowChart } from './CashFlowChart';
import { ExpenseBreakdown } from './ExpenseBreakdown';
import { ExpenseDrilldown } from './ExpenseDrilldown';
import { GSTSummaryWidget } from './GSTSummaryWidget';
import { AIInsightsPanel } from './AIInsightsPanel';
import { RecentTransactions } from './RecentTransactions';
import { DateRangeSelector } from './DateRangeSelector';

function ymNow() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
}

export function OverviewPage() {
  const { workspace } = useWorkspace();
  const connectedTool = useWorkspaceSyncTool(workspace);
  const [months, setMonths] = useState(12);
  const [expensePeriod, setExpensePeriod] = useState(() => ymNow());
  const [lastRefreshAt, setLastRefreshAt] = useState(Date.now());
  const [drillLedger, setDrillLedger] = useState(null);
  const [drillTxns, setDrillTxns] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);

  const { loading, error, data, reload } = useOverviewDashboard(workspace, {
    months,
    expensePeriod,
  });

  const refreshAll = useCallback(async () => {
    await reload();
    setLastRefreshAt(Date.now());
  }, [reload]);

  useEffect(() => {
    if (!loading && data && Object.keys(data).length) {
      setLastRefreshAt(Date.now());
    }
  }, [loading, data]);

  useAutoRefresh(refreshAll, REFRESH_INTERVAL_MS, true);

  const syncedCount = data?.metrics?.syncedTransactionCount;
  const noSyncedData =
    !loading &&
    !error &&
    data?.metrics &&
    (syncedCount === 0 ||
      (syncedCount === undefined && (data.transactions?.transactions?.length ?? 0) === 0));

  const openDrill = async (ledger) => {
    if (!ledger || ledger === 'Other') {
      setDrillLedger(ledger === 'Other' ? 'Other' : null);
      setDrillTxns([]);
      return;
    }
    setDrillLedger(ledger);
    setDrillLoading(true);
    try {
      const d = await fetchExpenseDrilldown(workspace, ledger);
      setDrillTxns(d.transactions || []);
    } catch {
      setDrillTxns([]);
    } finally {
      setDrillLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Financial overview</h1>
          <p className="text-sm text-slate-600">
            Data from Tally / Zoho after sync — reconciled entries only (MIS-07).
          </p>
        </div>
      </div>

      {error && !data?.metrics && (
        <ErrorState message={error} onRetry={refreshAll} />
      )}

      <SyncStatusBar
        onManualRefresh={refreshAll}
        autoRefreshProgress={{ lastRefreshAt, intervalMs: REFRESH_INTERVAL_MS }}
        connectedTool={connectedTool}
        pulseKey={lastRefreshAt}
      />

      {noSyncedData ? (
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <EmptyState
              title="No reconciled data yet"
              description="Sync from Bookkeeping so the dashboard can show Tally / Zoho entries only (MIS-07)."
              actionLabel="Go to Bookkeeping"
              actionTo="/bookkeeping"
            />
          </div>
          <AccountingSyncStatus workspace={workspace} onSynced={refreshAll} />
        </div>
      ) : null}

      {!noSyncedData ? (
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <div className="min-w-0 flex-1 space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <DateRangeSelector months={months} onChange={setMonths} />
              <label className="flex items-center gap-1 text-xs text-slate-600">
                Expense month
                <input
                  type="month"
                  className="rounded-lg border border-slate-300 px-2 py-1 text-sm"
                  value={expensePeriod}
                  onChange={(e) => setExpensePeriod(e.target.value)}
                />
              </label>
            </div>
          </div>

          {loading && !data?.metrics ? (
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <CardSkeleton key={i} />
              ))}
            </div>
          ) : (
            <KPICards metrics={data.metrics} />
          )}

          <div className="grid gap-4 xl:grid-cols-2">
            {loading && !data?.pnl?.months?.length ? (
              <LoadingSkeleton className="h-80 w-full" />
            ) : (
              <MonthlyPnLChart months={data.pnl?.months} />
            )}
            {loading && !data?.revenueTrend?.series?.length ? (
              <LoadingSkeleton className="h-80 w-full" />
            ) : (
              <RevenueTrendChart series={data.revenueTrend?.series} />
            )}
          </div>

          {loading && !data?.cashflow?.series?.length ? (
            <LoadingSkeleton className="h-80 w-full" />
          ) : (
            <CashFlowChart series={data.cashflow?.series} />
          )}

          <div className="grid gap-4 lg:grid-cols-2">
            {loading && !data?.expenseBreakdown?.categories?.length ? (
              <LoadingSkeleton className="h-72 w-full" />
            ) : (
              <ExpenseBreakdown
                categories={data.expenseBreakdown?.categories}
                onSliceClick={openDrill}
              />
            )}
            {loading && !data?.gstSummary ? (
              <LoadingSkeleton className="h-48 w-full" />
            ) : (
              <GSTSummaryWidget byStatus={data.gstSummary?.byStatus} />
            )}
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <div className="lg:col-span-2">
              {loading && !data?.transactions?.transactions?.length ? (
                <LoadingSkeleton className="h-64 w-full" />
              ) : (
                <RecentTransactions transactions={data.transactions?.transactions} />
              )}
            </div>
            <div className="space-y-4">
              <AccountingSyncStatus workspace={workspace} onSynced={refreshAll} />
              {loading && !data?.insights ? (
                <LoadingSkeleton className="h-40 w-full" />
              ) : (
                <AIInsightsPanel insights={data.insights} />
              )}
            </div>
          </div>
        </div>
      </div>
      ) : null}

      {drillLedger && (
        <ExpenseDrilldown
          ledger={drillLedger}
          transactions={drillLedger === 'Other' ? [] : drillTxns}
          loading={drillLedger === 'Other' ? false : drillLoading}
          onClose={() => setDrillLedger(null)}
        />
      )}
    </div>
  );
}
